/**
 * Phone Number Normalization Utilities
 *
 * Ensures consistent phone number format across the application.
 * Entity IDs are normalized phone numbers (E.164 without + prefix).
 */

/**
 * Normalize WhatsApp chat ID or phone number to clean phone number
 *
 * Examples:
 *   "6596734436@c.us" → 6596734436
 *   "226374992314462@lid" → 226374992314462
 *   "+65 9133 4435" → 6591334435
 *   (65) 8785-5563 → 6587855563
 *   +1-555-123-4567 → 15551234567
 *   65 9673 4437 → 6596734437
 *
 * @param phoneOrChatId - Phone number or WhatsApp chat ID in any format
 * @returns Normalized phone number (digits only)
 */
export function normalizePhoneNumber(phoneOrChatId: string): string {
  if (!phoneOrChatId) {
    return '';
  }

  // Remove WhatsApp suffixes first (@c.us, @g.us, @lid, @broadcast)
  const withoutSuffix = phoneOrChatId.replace(/@(c\.us|g\.us|lid|broadcast)$/, '');

  // Remove all non-digit characters (spaces, dashes, parentheses, plus signs)
  const digitsOnly = withoutSuffix.replace(/\D/g, '');

  // If the result is empty or too short, return the original value (might be a username)
  if (digitsOnly.length < 6) {
    // Return a cleaned version of the original (remove non-alphanumeric)
    return withoutSuffix.replace(/[^a-zA-Z0-9]/g, '');
  }

  // For valid phone numbers (10-15 digits), return digits only
  if (digitsOnly.length <= 15) {
    return digitsOnly;
  }

  // If too long, truncate to 15 digits
  return digitsOnly.slice(0, 15);
}

/**
 * Format phone number for display
 *
 * Examples:
 *   6591334435 → +65 9133 4435 (Singapore)
 *   6587855563 → +65 8785 5563 (Singapore)
 *   15551234567 → +1 555 123 4567 (USA)
 *
 * @param normalized - Normalized phone number (digits only)
 * @returns Formatted phone number for display
 */
export function formatPhoneNumber(normalized: string): string {
  if (!normalized) {
    return 'Unknown';
  }

  // If it contains letters, it's likely a username, not a phone number
  if (/[a-zA-Z]/.test(normalized)) {
    return normalized;
  }

  // Singapore format (country code 65, 8-digit number)
  if (normalized.startsWith('65') && normalized.length === 10) {
    return `+65 ${normalized.slice(2, 6)} ${normalized.slice(6)}`;
  }

  // USA format (country code 1, 10-digit number)
  if (normalized.startsWith('1') && normalized.length === 11) {
    return `+1 ${normalized.slice(1, 4)} ${normalized.slice(4, 7)} ${normalized.slice(7)}`;
  }

  // UK format (country code 44)
  if (normalized.startsWith('44') && normalized.length >= 12) {
    return `+44 ${normalized.slice(2, 6)} ${normalized.slice(6)}`;
  }

  // If it looks like a valid phone number (all digits, length >= 10)
  if (/^\d+$/.test(normalized) && normalized.length >= 10) {
    // Generic format: +{country code} {number}
    // Try to detect country code (1-3 digits)
    if (normalized.length >= 11) {
      // Assume 2-digit country code
      return `+${normalized.slice(0, 2)} ${normalized.slice(2)}`;
    }

    // Default: just add + prefix
    return `+${normalized}`;
  }

  // For short or unusual formats, return as is
  return normalized;
}

/**
 * Validate phone number format
 *
 * @param phone - Phone number to validate
 * @returns true if valid, false otherwise
 */
export function isValidPhoneNumber(phone: string): boolean {
  if (!phone) {
    return false;
  }

  try {
    const normalized = normalizePhoneNumber(phone);
    // E.164 format validation: 10-15 digits
    return /^\d{10,15}$/.test(normalized);
  } catch {
    return false;
  }
}

/**
 * Extract phone number from various formats
 * Handles formats like:
 * - "6591334435"
 * - "entity-6591334435"
 * - "+6591334435"
 *
 * @param value - Value that might contain a phone number
 * @returns Normalized phone number or null
 */
export function extractPhoneNumber(value: string): string | null {
  if (!value) {
    return null;
  }

  // If it starts with "entity-", extract the phone part
  if (value.startsWith('entity-')) {
    const phoneCandidate = value.substring(7); // Remove "entity-" prefix
    if (isValidPhoneNumber(phoneCandidate)) {
      return normalizePhoneNumber(phoneCandidate);
    }
  }

  // Try to normalize the value directly
  if (isValidPhoneNumber(value)) {
    return normalizePhoneNumber(value);
  }

  return null;
}

/**
 * Get country code from normalized phone number
 *
 * @param normalized - Normalized phone number
 * @returns Country code (e.g., "65", "1", "44") or null
 */
export function getCountryCode(normalized: string): string | null {
  if (!normalized || normalized.length < 10) {
    return null;
  }

  // Common country codes
  const countryCodePatterns = [
    { code: '1', length: 1 },     // USA, Canada
    { code: '44', length: 2 },    // UK
    { code: '65', length: 2 },    // Singapore
    { code: '86', length: 2 },    // China
    { code: '91', length: 2 },    // India
    { code: '81', length: 2 },    // Japan
  ];

  for (const pattern of countryCodePatterns) {
    if (normalized.startsWith(pattern.code)) {
      return pattern.code;
    }
  }

  // Default: assume first 1-2 digits
  if (normalized.length >= 11) {
    return normalized.slice(0, 2);
  }

  return normalized.slice(0, 1);
}

/**
 * Convert entity ID to phone number
 * Handles backward compatibility with old entity ID formats
 *
 * @param entityId - Entity ID (could be phone or "entity-{phone}" or timestamp)
 * @returns Normalized phone number or the original entity ID
 */
export function entityIdToPhone(entityId: string): string {
  if (!entityId) {
    return entityId;
  }

  // Try to extract phone number
  const extracted = extractPhoneNumber(entityId);
  if (extracted) {
    return extracted;
  }

  // If it's already a valid phone number, return it
  if (isValidPhoneNumber(entityId)) {
    return normalizePhoneNumber(entityId);
  }

  // Return original if we can't extract a phone number
  return entityId;
}

/**
 * Convert phone number to entity ID
 * Entity IDs are normalized phone numbers
 *
 * @param phone - Phone number in any format
 * @returns Entity ID (normalized phone number)
 * @throws Error if phone number is invalid
 */
export function phoneToEntityId(phone: string): string {
  return normalizePhoneNumber(phone);
}
